import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { AccountingInvoiceSyncStatusFailureCodeEnum } from 'anchor-ts-infra/src/dto/accounting-invoice/model';
import { InvoiceDisplayStatusV2 } from 'anchor-scout-common/types';
import { Divider } from 'seagull';
import { Intent } from '~/infra/access-control';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { invoiceModel, relationshipModel } from '~/models';
import { InvoiceStatus } from '~/models/invoice';
import { InvoiceOptimisticUpdateType } from '~/providers/InvoiceChangesObservableProvider';
import { Accounting } from '~/types/api.model';
import { getAccountingInvoiceErrorMessage } from '~/utils/getAccountingInvoiceErrorMessage';
export const invoiceSearchParam = 'invoice';
export const relationshipSearchParam = 'relationship';
export const utilsTextsPath = 'components.fullPageInvoice.utils';
export function getFooter(relationship, invoice, texts) {
    const { paymentMethod, feeControls } = relationship;
    const isAdHoc = relationshipModel.isAdHoc(relationship);
    return compact([
        paymentMethod && invoiceModel.isPending(invoice) && {
            id: 'due',
            text: paymentMethod.last4 === '' ? texts.dueTextPendingDetails({
                date: invoiceModel.paymentProcessingDate(invoice)
            }) : texts.dueText({
                date: invoiceModel.paymentProcessingDate(invoice),
                type: paymentMethod.type,
                last4: paymentMethod.last4
            })
        },
        !feeControls.coverCreditCardFees && (isAdHoc || (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.type) === 'creditCard') && {
            id: 'cc',
            text: texts.creditCardFees()
        }
    ]);
}
export function getURLWithFullPageInvoice(config) {
    const { invoiceId, relationshipId, pathname, search } = config;
    const url = new URL(pathname || location.pathname, location.origin);
    const searchParams = new URLSearchParams(search || location.search);
    searchParams.forEach((value, key)=>url.searchParams.append(key, value));
    url.searchParams.set(invoiceSearchParam, invoiceId);
    url.searchParams.set(relationshipSearchParam, relationshipId);
    return url.pathname + url.search;
}
function syncRefundCallout(param, texts) {
    let { accounting, syncRefundAction } = param;
    return {
        id: 'sync-refund',
        intent: 'info',
        text: texts.refundReadyToSync({
            accountingName: getAccountingDetails(accounting).fullName
        }),
        status: 'pending',
        actions: [
            syncRefundAction
        ]
    };
}
function isItemLinked(item, serviceTemplates, accounting) {
    return getAccountingDetails(accounting).policy.checkServiceMapping({
        item,
        serviceTemplates,
        accounting
    });
}
const syncAfterFailureCallout = (param)=>{
    let { items, contacts, accounting, integration, relationship, retrySyncAction, serviceTemplates, mapContactsAction, mapServicesAction, retrySyncWithoutTimeActivitiesAction } = param;
    return (data)=>{
        const isClientContactLinked = getAccountingDetails(accounting).policy.checkContactsMapping({
            contacts,
            relationship,
            accounting
        });
        const allServicesLinked = items.every((item)=>{
            return isItemLinked(item, serviceTemplates, accounting);
        });
        const failureCode = data.failure.failureCode();
        const failedToMapServices = !allServicesLinked && [
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceServiceNotMapped,
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientAndServiceNotMapped
        ].includes(failureCode);
        const failedToMapContacts = !isClientContactLinked && [
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientNotMapped,
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientAndServiceNotMapped
        ].includes(failureCode);
        const failedToMapTimeActivities = AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrTimeActivityCannotAppliedToInvoice === failureCode;
        const mappingActions = compact([
            failedToMapContacts && mapContactsAction,
            failedToMapServices && mapServicesAction,
            ...failedToMapTimeActivities ? [
                retrySyncWithoutTimeActivitiesAction,
                retrySyncAction
            ] : []
        ]);
        return {
            id: 'sync-after-failure',
            intent: 'warning',
            text: getAccountingInvoiceErrorMessage({
                invoiceIntegration: integration,
                accounting
            }),
            status: 'error',
            actions: mappingActions.length > 0 ? mappingActions : [
                retrySyncAction
            ]
        };
    };
};
export function getAccountingSectionCallout(params, texts) {
    const { accounting, integration, invoiceStatus, numRefunds, isZeroAmount, syncAction, syncPaymentAction } = params;
    if (!integration) return null;
    return integration.accept({
        whenPendingSync: ()=>({
                id: 'ready-to-sync',
                intent: 'info',
                text: texts.invoiceReadyToSync({
                    accountingName: getAccountingDetails(accounting).fullName
                }),
                status: 'pending',
                actions: [
                    syncAction
                ]
            }),
        whenRefundSyncFailed: syncAfterFailureCallout(_object_spread_props(_object_spread({}, params), {
            integration
        })),
        whenInvoiceSyncFailed: syncAfterFailureCallout(_object_spread_props(_object_spread({}, params), {
            integration
        })),
        whenPaymentSyncFailed: syncAfterFailureCallout(_object_spread_props(_object_spread({}, params), {
            integration
        })),
        whenInvoiceRecorded: ()=>{
            if ([
                InvoiceStatus.PaymentConfirmed,
                InvoiceStatus.Paid
            ].includes(invoiceStatus) && !isZeroAmount) return {
                id: 'payment-sync-failed',
                intent: 'info',
                text: texts.paymentReadyToSync({
                    accountingName: getAccountingDetails(accounting).fullName
                }),
                status: 'pending',
                actions: [
                    syncPaymentAction
                ]
            };
            return null;
        },
        whenVoidedRecorded: ()=>null,
        whenPaymentRecorded: ()=>{
            if (invoiceStatus === InvoiceStatus.Refunded || invoiceStatus === InvoiceStatus.PartiallyRefunded) return syncRefundCallout(params, texts);
            return null;
        },
        whenRefundRecorded: (refundRecorded)=>{
            if (refundRecorded.numRefunds != null && numRefunds != null && refundRecorded.numRefunds < numRefunds) return syncRefundCallout(params, texts);
            return null;
        }
    });
}
export function makeResumePaymentInvoiceAction(isTerminatedRelationship, texts) {
    return {
        text: texts.resumePayment(),
        intent: Intent.InvoiceResumePayment,
        disabled: isTerminatedRelationship,
        tooltipTitle: isTerminatedRelationship ? texts.terminated() : undefined
    };
}
export const getGenericAccountingTooltip = (accounting, itemName, texts)=>/*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            texts.lineItemMappedTo({
                accountingName: getAccountingDetails(accounting).fullName
            }),
            " ",
            /*#__PURE__*/ _jsx("b", {
                children: itemName
            })
        ]
    });
export const getNotVisibleToClientTooltip = (texts)=>texts.notVisibleToClient();
export const getXeroTooltip = (param, texts)=>{
    let { accounting, itemName, testId } = param;
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": testId,
        children: [
            getGenericAccountingTooltip(accounting, itemName, texts),
            ". ",
            getNotVisibleToClientTooltip(texts)
        ]
    });
};
export const getItemQBOTooltip = (qbo, testId, texts)=>/*#__PURE__*/ _jsxs("div", {
        "data-testid": testId,
        children: [
            getGenericAccountingTooltip(Accounting.QBO, qbo.item.name, texts),
            qbo.class && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    ' ',
                    texts.withTheClass(),
                    " ",
                    /*#__PURE__*/ _jsx("b", {
                        children: qbo.class.name
                    })
                ]
            }),
            ". ",
            getNotVisibleToClientTooltip(texts)
        ]
    });
export function onRequestRefundSuccess(param) {
    let { now, reason, invoice, sellerId, onRefresh, setInvoice } = param;
    if (invoiceModel.isPaid(invoice) || invoiceModel.isConfirmed(invoice)) {
        const timestamp = now.getTime();
        setInvoice(invoiceModel.setRefunded(invoice, timestamp, now));
        onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.Refunded,
                invoiceId: invoice.id,
                timestamp
            }
        });
    } else {
        setInvoice(invoiceModel.setRefundRequested(invoice));
        onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.RefundRequested,
                invoiceId: invoice.id,
                userId: sellerId,
                reason
            }
        });
    }
}
export function onRequestPaymentConfirmed(param) {
    let { invoice, isVendor, onRefresh, setInvoice } = param;
    setInvoice(invoiceModel.setPaymentConfirmed(invoice, isVendor));
    onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
        optimisticUpdate: {
            type: InvoiceOptimisticUpdateType.PaymentConfirmed,
            invoiceId: invoice.id,
            isVendor
        }
    });
}
export function showRequestPaymentMethodModal(invoice, relationship) {
    if (relationshipModel.isTerminated(relationship)) return false;
    const status = invoiceModel.displayStatus(invoice);
    return invoiceModel.isPaymentMethodUnset(invoice) && (status === InvoiceDisplayStatusV2.Overdue || status === InvoiceDisplayStatusV2.Issued);
}
export function getBadgeSkin(status) {
    switch(status){
        case InvoiceDisplayStatusV2.Issued:
        case InvoiceDisplayStatusV2.Pending:
        case InvoiceDisplayStatusV2.Voided:
        case InvoiceDisplayStatusV2.Refunded:
        case InvoiceDisplayStatusV2.PartiallyRefunded:
            return 'default';
        case InvoiceDisplayStatusV2.Processing:
        case InvoiceDisplayStatusV2.Paid:
            return 'success';
        case InvoiceDisplayStatusV2.Overdue:
        case InvoiceDisplayStatusV2.RefundFailed:
        case InvoiceDisplayStatusV2.Disputed:
            return 'error';
    }
}
export const onRequestPartialRefundSuccess = (param)=>{
    let { now, reason, invoice, sellerId, refundFees, refundAmount, onRefresh, setInvoice } = param;
    if (!invoice.payment) return;
    const refunds = invoice.payment.refunds();
    const isFullyRefunded = refunds && refunds.length > 0 ? new Decimal(refunds[refunds.length - 1].amounts.remainingAmount).div(100).sub(new Decimal(refundAmount)).eq(0) : invoiceModel.rawTotalAmount(invoice).eq(new Decimal(refundAmount));
    if (invoiceModel.isPaid(invoice) || invoiceModel.isConfirmed(invoice) || invoiceModel.isPartiallyRefunded(invoice)) {
        const timestamp = now.getTime();
        setInvoice(isFullyRefunded ? invoiceModel.setRefunded(invoice, timestamp, now) : invoiceModel.setPartiallyRefunded(invoice, {
            timestamp,
            fees: invoice.payment.fees(),
            paymentProcessorType: invoice.payment.paymentProcessorType(),
            reason,
            userId: sellerId,
            refundAmount,
            refundFees,
            rawTotalAmount: invoiceModel.rawTotalAmount(invoice)
        }));
        onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
            optimisticUpdate: _object_spread_props(_object_spread({}, isFullyRefunded ? {
                type: InvoiceOptimisticUpdateType.Refunded
            } : {
                type: InvoiceOptimisticUpdateType.PartiallyRefunded,
                reason,
                refundAmount,
                refundFees,
                fees: invoice.payment.fees(),
                paymentProcessorType: invoice.payment.paymentProcessorType(),
                userId: sellerId,
                rawTotalAmount: invoiceModel.rawTotalAmount(invoice)
            }), {
                invoiceId: invoice.id,
                timestamp
            })
        });
    } else {
        setInvoice(invoiceModel.setPartialRefundRequested(invoice));
        onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
            optimisticUpdate: {
                type: isFullyRefunded ? InvoiceOptimisticUpdateType.RefundRequested : InvoiceOptimisticUpdateType.PartialRefundRequested,
                invoiceId: invoice.id,
                userId: sellerId,
                refundAmount,
                refundFees,
                reason
            }
        });
    }
};
export const getServiceTemplateIds = (invoice)=>{
    return compact(invoice.items.map((item)=>item.serviceTemplateId));
};
export const MobileDivider = ()=>/*#__PURE__*/ _jsx(Divider, {
        margin: 16
    });
